import { makeT } from '../../group_leader/helpers/i18n'
import { PLACEHOLDER } from '../../group_leader/helpers/value'
import { entryGross, entryToPar } from './results'

const t = makeT('group_leader.event_results.leaderboard')

// The blob says which columns exist, as in the monolith's result_scope partials; affiliation goes under the name.
const COLUMN_ORDER = [ 'position', 'name', 'score', 'thru', 'total', 'purse', 'points', 'details' ]

// Numeric columns are right-aligned with tabular figures (DataTable's `numeric`).
const NUMERIC_COLUMNS = new Set([ 'score', 'thru', 'total', 'purse', 'points' ])

// Every other column's label key is `col_<key>`.
const LABEL_KEYS = { position: 'col_pos', name: 'col_player' }

const CELL_CLASS = {
  purse: 'gl-event-results-lb-td--purse',
  points: 'gl-event-results-lb-td--points',
}

// `column_names` can carry presentation HTML ("To Par<br/>Net"); flattened, never injected.
const cleanHeader = (raw) =>
  (raw || '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/\s+/g, ' ')
    .trim()

// The server's own label first: score_scope's name literal already encodes team-ness.
const makeColumn = (key, rawLabel, fallback) => ({
  key,
  label: cleanHeader(rawLabel) || fallback || t(LABEL_KEYS[key] || `col_${key}`),
  numeric: NUMERIC_COLUMNS.has(key),
  className: CELL_CLASS[key],
})

// by_match.rb:12 hides the position column, and the blob then omits both the flag and the label.
const showsPosition = (leaderboard) => {
  const visibility = leaderboard?.event?.column_visibility
  if (visibility) { return Boolean(visibility.position) }

  return Boolean(leaderboard?.event?.column_names?.position)
}

// Cached blobs predating `column_visibility` keep the old to-par / gross heuristic.
const legacyColumns = (entries, names) => {
  const columns = names.position ? [ makeColumn('position', names.position) ] : []
  columns.push(makeColumn('name', names.name))
  const first = entries[0]

  if (first && entryToPar(first) !== null && entryGross(first) !== null) {
    columns.push(makeColumn('score', names.score, t('col_to_par')))
    columns.push(makeColumn('total', names.total, t('col_total')))
  } else {
    columns.push(makeColumn('score', names.score, t('col_score')))
  }
  return columns
}

export const buildColumns = (leaderboard, entries) => {
  const visibility = leaderboard?.event?.column_visibility
  const names = leaderboard?.event?.column_names || {}
  if (!visibility) { return legacyColumns(entries, names) }

  const columns = COLUMN_ORDER
    .filter((key) => visibility[key])
    .map((key) => makeColumn(key, names[key]))

  // The name column is the row anchor, kept even if the server omits its flag.
  if (!columns.some((column) => column.key === 'name')) {
    columns.unshift(makeColumn('name', names.name))
  }
  return columns
}

export const cellValue = (entry, key) => entry[key] ?? PLACEHOLDER

export const entryName = (entry) => entry.name || entry.player_name || entry.full_name || PLACEHOLDER

// Shown under the golfer's name, as every monolith leaderboard does; blank for most rows.
export const entryAffiliation = (entry) => (entry.affiliation || '').trim()

// The format's most meaningful figure: emphasised, and the only one a phone has room for.
export const primaryColumnKey = (columns) =>
  [ 'score', 'points', 'purse', 'total' ].find((key) => columns.some((column) => column.key === key)) || null

// The name, match score and thru columns both match formats show, labelled as buildColumns does.
const matchColumns = (leaderboard) => {
  const names = leaderboard?.event?.column_names || {}
  return [
    { key: 'name', label: cleanHeader(names.name) || t('col_player') },
    { key: 'score', label: cleanHeader(names.score) || t('col_match'), numeric: true },
    { key: 'thru', label: t('col_thru'), numeric: true },
  ]
}

export const matchPlayColumns = (leaderboard) => {
  const [ name, score, thru ] = matchColumns(leaderboard)
  return [ name, score, { key: 'opponent', label: t('col_opponent') }, thru ]
}

export const multiMatchColumns = (leaderboard) => [
  ...(showsPosition(leaderboard) ? [ { key: 'position', label: t('col_pos') } ] : []),
  ...matchColumns(leaderboard),
]
